/*
 * Based on three.js DepthLimitedBlurShader.js
 * Modifications copyright Higharc Inc. 2019
 */
import * as THREE from "three";
import type {
  ScreenSpaceViewNormalDefines,
  ScreenSpaceViewNormalUniforms,
  ScreenSpaceViewPositionDefines,
  ScreenSpaceViewPositionUniforms,
} from "./screenSpaceHelpersGLSL";
import {
  screenSpaceViewNormalDefineDefaults,
  screenSpaceViewNormalGLSL,
  screenSpaceViewNormalUniformDefaults,
  screenSpaceViewPositionDefineDefaults,
  screenSpaceViewPositionGLSL,
  screenSpaceViewPositionUniformDefaults,
} from "./screenSpaceHelpersGLSL";
import { UVVertexShader } from "./UVVertexShader";

interface DepthLimitedBlurShader2Defines extends ScreenSpaceViewPositionDefines, ScreenSpaceViewNormalDefines {
  [key: string]: number;
  KERNEL_RADIUS: number; // eslint-disable-line @typescript-eslint/naming-convention
  NORMAL_CUTOFF: number; // eslint-disable-line @typescript-eslint/naming-convention
  DEPTH_CUTOFF_BASED_ON_NORMAL: number; // eslint-disable-line @typescript-eslint/naming-convention
}

interface DepthLimitedBlurShader2Uniforms extends ScreenSpaceViewPositionUniforms, ScreenSpaceViewNormalUniforms {
  [key: string]: THREE.IUniform;
  tDiffuse: THREE.IUniform;

  invSize: THREE.IUniform;

  samplePixelOffsets: THREE.IUniform;
  sampleWeights: THREE.IUniform;

  depthCutoff: THREE.IUniform;
  colorMask: THREE.IUniform;

  normalDotProductCutoff: THREE.IUniform;
}

interface DepthLimitedBlurShader2Type {
  defines: DepthLimitedBlurShader2Defines;
  uniforms: DepthLimitedBlurShader2Uniforms;
  vertexShader: string;
  fragmentShader: string;
}

export const DepthLimitedBlurShader2: DepthLimitedBlurShader2Type = {
  defines: {
    ...screenSpaceViewPositionDefineDefaults,
    ...screenSpaceViewNormalDefineDefaults,
    KERNEL_RADIUS: 4,
    NORMAL_CUTOFF: 1,
    DEPTH_CUTOFF_BASED_ON_NORMAL: 1,
  },
  uniforms: {
    ...screenSpaceViewPositionUniformDefaults,
    ...screenSpaceViewNormalUniformDefaults,

    tDiffuse: { value: null },

    // Inverse of render target size in pixels - can also be thought as the size of one pixel in UV space.
    invSize: { value: new THREE.Vector2(1 / 512, 1 / 512) },

    samplePixelOffsets: { value: [new THREE.Vector2(0, 0)] },
    sampleWeights: { value: [1.0] },

    depthCutoff: { value: 10 },
    colorMask: { value: [true, true, true, true] },

    normalDotProductCutoff: { value: Math.cos(Math.PI * 0.3) },
  },
  vertexShader: UVVertexShader,
  fragmentShader: `#include <common>
    #include <packing>

    #if NORMAL_CUTOFF == 1 || DEPTH_CUTOFF_BASED_ON_NORMAL == 1
    ${screenSpaceViewNormalGLSL}
    #endif

    ${screenSpaceViewPositionGLSL}

    uniform sampler2D tDiffuse;
    #if NORMAL_CUTOFF == 1
    uniform float normalDotProductCutoff;
    #endif

    uniform float depthCutoff;

    uniform vec2 samplePixelOffsets[ KERNEL_RADIUS + 1 ];
    uniform float sampleWeights[ KERNEL_RADIUS + 1 ];

    uniform bvec4 colorMask;

    varying vec2 vUv;
    uniform vec2 invSize;

    #if DEPTH_CUTOFF_BASED_ON_NORMAL == 1
    // Predict a neighboring pixel's view z value based on this pixel's normal.
    float predictNeighborViewZ( const in vec2 screenPosition, const in float viewZ, const vec3 viewNormal, const vec2 neighborPixelOffset ) {
      #if PERSPECTIVE_CAMERA == 1
        // Get the view space ray corresponding to one of the neighboring pixels.
        vec2 neighborScreenPosition = screenPosition + invSize * neighborPixelOffset;
        vec4 rayClip = vec4(neighborScreenPosition * 2.0 - 1.0, -1.0, 1.0);
        vec3 rayViewDir = normalize(vec3((cameraInverseProjectionMatrix * rayClip).xy, -1.0));
        // Intersect the ray with the view space plane defined by the view space position of the center pixel and the normal.
        float planeDistFromOrigin = dot(viewNormal, getViewPosition(screenPosition, viewZ));
        float t = planeDistFromOrigin / dot(viewNormal, rayViewDir);
        return (t * rayViewDir).z;
      #else
        // TODO:
        return viewZ;
      #endif
    }
    #endif // DEPTH_CUTOFF_BASED_ON_NORMAL

    void main() {
      float depth = GET_DEPTH(tDepth, vUv);
      if(IS_DEPTH_OUT_OF_BOUNDS(depth)) {
        discard;
      }
      float centerViewZ = -getViewZ(depth);
    #if NORMAL_CUTOFF == 1 || DEPTH_CUTOFF_BASED_ON_NORMAL == 1
      vec3 centerNormal = GET_VIEW_NORMAL(getViewPosition(vUv, viewZ), vUv);
    #endif

      bool rBreak = false, lBreak = false;

      float weightSum = sampleWeights[0];
      vec4 centerColor = texture2D( tDiffuse, vUv );
      vec4 diffuseSum = centerColor * weightSum;

    #if DEPTH_CUTOFF_BASED_ON_NORMAL == 1
      vec2 prevRSampleUv = vUv;
      vec2 prevLSampleUv = vUv;
      float prevRSampleViewZ = centerViewZ;
      float prevLSampleViewZ = centerViewZ;
    #endif

      for( int i = 1; i <= KERNEL_RADIUS; i ++ ) {

        float sampleWeight = sampleWeights[i];
        vec2 sampleUvOffset = samplePixelOffsets[i] * invSize;

        vec2 sampleUv = vUv + sampleUvOffset;
        float viewZ = -getViewZ(GET_DEPTH(tDepth, sampleUv));

      #if DEPTH_CUTOFF_BASED_ON_NORMAL == 1
        if( abs( viewZ - predictNeighborViewZ(prevRSampleUv, prevRSampleViewZ, centerNormal, samplePixelOffsets[1]) ) > depthCutoff ) rBreak = true;
      #else
        if( abs( viewZ - centerViewZ ) > depthCutoff ) rBreak = true;
      #endif
      #if NORMAL_CUTOFF == 1
        vec3 normal = GET_VIEW_NORMAL(getViewPosition(sampleUv, viewZ), sampleUv);
        if( dot( normal, centerNormal ) < normalDotProductCutoff ) rBreak = true;
      #endif

        if( ! rBreak ) {
          diffuseSum += texture2D( tDiffuse, sampleUv ) * sampleWeight;
          weightSum += sampleWeight;

        #if DEPTH_CUTOFF_BASED_ON_NORMAL == 1
          prevRSampleUv = sampleUv;
          prevRSampleViewZ = viewZ;
        #endif
        }

        sampleUv = vUv - sampleUvOffset;
        viewZ = -getViewZ(GET_DEPTH(tDepth, sampleUv));

      #if DEPTH_CUTOFF_BASED_ON_NORMAL == 1
        if( abs( viewZ - predictNeighborViewZ(prevLSampleUv, prevLSampleViewZ, centerNormal, -samplePixelOffsets[1]) ) > depthCutoff ) lBreak = true;
      #else
        if( abs( viewZ - centerViewZ ) > depthCutoff ) lBreak = true;
      #endif
      #if NORMAL_CUTOFF == 1
        normal = GET_VIEW_NORMAL(getViewPosition(sampleUv, viewZ), sampleUv);
        if( dot( normal, centerNormal ) < normalDotProductCutoff ) lBreak = true;
      #endif

        if( ! lBreak ) {
          diffuseSum += texture2D( tDiffuse, sampleUv ) * sampleWeight;
          weightSum += sampleWeight;

        #if DEPTH_CUTOFF_BASED_ON_NORMAL == 1
          prevLSampleUv = sampleUv;
          prevLSampleViewZ = viewZ;
        #endif
        }

      }

      gl_FragColor = centerColor * vec4( not( colorMask ) ) + diffuseSum / weightSum * vec4( colorMask );
    }`,
};

export const BlurShaderUtils2 = {
  createSampleWeights: function (kernelRadius: number, stdDev: number) {
    const gaussian = function (x, stdDev) {
      return Math.exp(-(x * x) / (2.0 * (stdDev * stdDev))) / (Math.sqrt(2.0 * Math.PI) * stdDev);
    };

    const weights: number[] = [];

    for (let i = 0; i <= kernelRadius; i++) {
      weights.push(gaussian(i, stdDev));
    }

    return weights;
  },

  createSampleOffsets: function (kernelRadius: number, pixelIncrement: THREE.Vector2) {
    const offsets: THREE.Vector2[] = [];

    for (let i = 0; i <= kernelRadius; i++) {
      offsets.push(pixelIncrement.clone().multiplyScalar(i));
    }

    return offsets;
  },

  normalDotProductCutoffFromAngle: function (angleInRadians: number) {
    // In the case of unit vectors such as normals:
    // cos( theta ) = dot( a, b )
    return Math.cos(angleInRadians);
  },

  configureKernel: function (
    material: THREE.ShaderMaterial,
    kernelRadius: number,
    stdDev: number,
    pixelIncrement: THREE.Vector2,
  ) {
    if (material.defines["KERNEL_RADIUS"] !== kernelRadius) {
      material.defines["KERNEL_RADIUS"] = kernelRadius;
      material.needsUpdate = true;
    }
    material.uniforms["samplePixelOffsets"].value = BlurShaderUtils2.createSampleOffsets(kernelRadius, pixelIncrement);
    material.uniforms["sampleWeights"].value = BlurShaderUtils2.createSampleWeights(kernelRadius, stdDev);
  },
};
